import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { APP_VERSION } from '@shared/tokens/app-version.token';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  public mandatoryVersion$: Observable<string | null>;
  public mandatoryUpdate$: Observable<boolean>;
  #mandatoryVersionSubject = new BehaviorSubject<string | null>(null);
  public readonly appVersion = inject(APP_VERSION);

  public constructor() {
    this.mandatoryVersion$ = this.#mandatoryVersionSubject.asObservable();
    this.mandatoryUpdate$ = this.createMandatoryUpdateObservable();
  }

  public setMandatoryVersion(mandatoryVersion: string): void {
    this.#mandatoryVersionSubject.next(mandatoryVersion);
  }

  private createMandatoryUpdateObservable(): Observable<boolean> {
    return this.mandatoryVersion$.pipe(
      map(mandatory => {
        if (!mandatory) {
          return false;
        }
        const current = this.appVersion;
        return this.isVersionOutdated(current, mandatory);
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private isVersionOutdated(version: string, newVersion: string): boolean {
    return this.compareSemanticVersions(version, newVersion) < 0;
  }

  /**
   * Compare semantic version strings (e.g., "1.45.8" vs "1.46.0")
   * Returns: -1 if v1 < v2, 0 if equal, 1 if v1 > v2
   */
  private compareSemanticVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(p => parseInt(p, 10));
    const parts2 = v2.split('.').map(p => parseInt(p, 10));

    const maxLength = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < maxLength; i++) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;

      if (part1 < part2) {
        return -1;
      }
      if (part1 > part2) {
        return 1;
      }
    }

    return 0;
  }
}
